import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Group as GroupIcon, Psychology as PsychologyIcon, Edit as CustomPromptIcon, Search as SearchIcon } from "@mui/icons-material";
import BaseInstance from "../../../../Services/AxiosInstance/BaseInstance";
import { MODELS } from "../../../../Services/Endpoint";

interface NodeTemplate {
  id: string;
  label: string;
  description: string;
  category: 'Team' | 'LLM Models' | 'CustomPrompt';
  icon: React.ReactNode;
  provider?: string;
  models?: string[];
}

const nodeTemplates: NodeTemplate[] = [
  {
    id: 'team',
    label: 'Team',
    description: 'Create a team of agents that work together',
    category: 'Team',
    icon: <GroupIcon />,
  },
  {
    id: 'custom-prompt',
    label: 'System Prompt',
    description: 'Create custom system prompts for agents',
    category: 'CustomPrompt',
    icon: <CustomPromptIcon />,
  },
  // LLM Models will be loaded dynamically from API
];

const categories = ['Team', 'LLM Models', 'CustomPrompt'];

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Team':
      return <GroupIcon />;
    case 'LLM Models':
      return <PsychologyIcon />;
    case 'CustomPrompt':
      return <CustomPromptIcon />;
    default:
      return <GroupIcon />;
  }
};

const getCategoryColor = (category: string) => {
  return '#6B7280'; // Light blue-gray for all categories
};

const NodePalette: React.FC = () => {
  const [providers, setProviders] = useState<Record<string, string[]>>({});
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch providers on component mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const response = await BaseInstance.get(MODELS);
        setProviders(response.data);
      } catch (error) {
        console.error('Failed to fetch providers:', error);
        // Fallback to hardcoded data for testing
        const fallbackProviders = {
          "OpenAI": ["gpt-4o", "gpt-4o-mini", "gpt-4", "gpt-3.5-turbo"],
          "Anthropic": ["claude-3-opus", "claude-3-sonnet", "claude-3-haiku"],
          "Google": ["gemini-1.5-pro", "gemini-1.5-flash", "gemini-1.0-pro"]
        };
        setProviders(fallbackProviders);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, []);

  // Create dynamic provider templates (LLM Models)
  const providerTemplates: NodeTemplate[] = Object.entries(providers).map(([provider, models]) => ({
    id: `provider-${provider.toLowerCase()}`,
    label: provider === 'OpenAI' ? 'Models' : provider,
    description: `${provider} models`,
    category: 'LLM Models' as const,
    icon: <PsychologyIcon />,
    provider: provider,
    models: Array.isArray(models) ? models : [],
  }));

  // Combine static and dynamic templates
  const allTemplates = [...nodeTemplates, ...providerTemplates];

  // Filter nodes based on search term
  const filteredNodeTemplates = allTemplates.filter(node =>
    node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    node.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const onDragStart = (event: React.DragEvent, nodeTemplate: NodeTemplate) => {
    // Create a serializable version of the template without React elements
    const serializableTemplate = {
      id: nodeTemplate.id,
      label: nodeTemplate.label,
      description: nodeTemplate.description,
      category: nodeTemplate.category,
      provider: nodeTemplate.provider,
      models: nodeTemplate.models,
    };
    event.dataTransfer.setData('application/reactflow', JSON.stringify(serializableTemplate));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: 280,
        height: '100%',
        borderRadius: 0,
        borderRight: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search nodes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <Box sx={{ overflow: 'auto', height: 'calc(100% - 80px)', p: 1.5 }}>
        {categories.map((category) => {
          const categoryNodes = filteredNodeTemplates.filter(node => node.category === category);
          const categoryColor = getCategoryColor(category);

          // Don't show category if no nodes match the search
          if (categoryNodes.length === 0) return null;

          return (
            <Box key={category} sx={{ mb: 2.5 }}>
              {/* Category Title */}
              <Typography
                variant="caption"
                sx={{
                  color: '#9CA3AF',
                  fontWeight: 500,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1.5,
                  px: 1,
                  display: 'block'
                }}
              >
                {category}
              </Typography>

              {/* Category Items */}
              <List dense sx={{ py: 0 }}>
                {categoryNodes.map((node) => (
                  <ListItem key={node.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      draggable
                      onDragStart={(e) => onDragStart(e, node)}
                      sx={{
                        py: 0.75,
                        px: 1,
                        borderRadius: 0.75,
                        minHeight: 40,
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 28,
                          mr: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 0.5,
                          backgroundColor: '#F3F4F6',
                          color: categoryColor,
                        }}
                      >
                        {node.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              color: '#374151',
                              fontSize: '13px',
                              lineHeight: 1.2,
                            }}
                          >
                            {node.label}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default NodePalette;
